import { HubConnectionState, type HubConnection } from '@microsoft/signalr';
import type { HandlerSummary } from './SignalR.types';

/** Internal HttpConnection shape (not exposed by @microsoft/signalr types). */
export interface SignalRHttpConnection {
  onreceive?: (data: string | ArrayBuffer) => void;
  transport?: SignalRTransport;
}

export interface SignalRTransport {
  _socket?: WebSocket;
  socket?: WebSocket;
  _webSocket?: WebSocket;
  webSocket?: WebSocket;
}

export interface HubConnectionInternals {
  connection?: SignalRHttpConnection;
  _handlers?: HandlerMapSource;
  handlers?: HandlerMapSource;
  _callbacks?: HandlerMapSource;
  callbacks?: HandlerMapSource;
}

type HandlerMapSource = Map<string, unknown> | Record<string, unknown>;

export const getHubConnectionInternals = (
  connection: HubConnection,
): HubConnectionInternals => connection as unknown as HubConnectionInternals;

export const getHttpConnection = (
  connection: HubConnection,
): SignalRHttpConnection | undefined =>
  getHubConnectionInternals(connection).connection;

export const getWebSocketFromConnection = (
  connection: HubConnection,
): WebSocket | undefined => {
  const transport = getHttpConnection(connection)?.transport;
  if (!transport) {
    return undefined;
  }

  const socket =
    transport._socket ??
    transport.socket ??
    transport._webSocket ??
    transport.webSocket;

  return socket instanceof WebSocket ? socket : undefined;
};

const toHandlerMap = (handlers: HandlerMapSource): Map<string, unknown> =>
  handlers instanceof Map ? handlers : new Map(Object.entries(handlers));

const countHandlers = (handlerList: unknown): number => {
  if (Array.isArray(handlerList)) {
    return handlerList.length;
  }
  return handlerList ? 1 : 0;
};

export const inspectConnectionHandlers = (
  hubName: string,
  connection: HubConnection,
): HandlerSummary => {
  console.log(`\n🔍 Inspecting handlers for hub [${hubName}]:`);
  console.log(`   Connection State: ${connection.state}`);

  const internals = getHubConnectionInternals(connection);
  const handlersInternal =
    internals._handlers ??
    internals.handlers ??
    internals._callbacks ??
    internals.callbacks;

  const summary: HandlerSummary = {
    connected: connection.state === HubConnectionState.Connected,
    handlers: {},
    internalKeys: [],
  };

  if (handlersInternal) {
    const handlerMap = toHandlerMap(handlersInternal);

    if (handlerMap.size === 0) {
      console.log('   📭 No handlers registered');
    } else {
      console.log(`   📡 Found ${handlerMap.size} registered handler(s):`);
      handlerMap.forEach((handlerList, methodName) => {
        const count = countHandlers(handlerList);
        summary.handlers[methodName] = count;
        console.log(`      • ${methodName}: ${count} handler(s)`);
      });
    }
  } else {
    console.log(
      '   ⚠️ Could not access handlers directly. Trying alternative methods...',
    );

    const keys = Object.keys(internals).filter(
      (key) =>
        key.toLowerCase().includes('handler') ||
        key.toLowerCase().includes('callback') ||
        key.toLowerCase().includes('method'),
    );

    summary.internalKeys = keys;

    if (keys.length > 0) {
      console.log('   🔑 Found potential handler-related properties:', keys);
    } else {
      console.log(
        '   💡 Tip: Handlers are registered but not directly accessible via public API.',
      );
      console.log(
        '   💡 Use connection.on() to register and connection.off() to unregister.',
      );
    }
  }

  console.log('');
  return summary;
};

export const logRegisteredHandlers = (
  hubName: string,
  connection: HubConnection,
): void => {
  const internals = getHubConnectionInternals(connection);
  const handlers =
    internals._handlers ??
    internals.handlers ??
    internals._callbacks ??
    internals.callbacks;

  if (!handlers) {
    return;
  }

  const handlerMap = toHandlerMap(handlers);
  if (handlerMap.size === 0) {
    return;
  }

  console.log(`📡 [${hubName}] Registered handlers:`);
  handlerMap.forEach((handlerList, methodName) => {
    console.log(`   • ${methodName}: ${countHandlers(handlerList)} handler(s)`);
  });
};
